/**
 * How an event's BU (Basic Information) constrains its targets (Set Target), per the Event Hub
 * testing feedback:
 *
 * - BU is All — every BU picked, or none (not narrowed yet) — so targets may be Overall or By BU,
 *   and By BU lists every BU.
 * - BU is narrowed to specific BUs — targets can only be By BU, and only those BUs get a row.
 *   An Overall number would claim a scope wider than the event's own.
 *
 * A BU removed from the event loses its target on save: the row is gone, so a number the marketer
 * can no longer see must not be sent. It is kept in the form's state meanwhile, so picking the BU
 * again brings the number back.
 */
import { BUSINESS_UNITS } from '../businessUnits';

export type TargetMode = 'overall' | 'byBu';

/** True when the event's BU is All — every BU picked, or none picked yet. */
export function isAllBusinessUnits(selected: readonly string[]): boolean {
  return selected.length === 0 || BUSINESS_UNITS.every((bu) => selected.includes(bu));
}

/** The BUs that get a By BU row, in the canonical list's order. */
export function targetBusinessUnits(selected: readonly string[]): readonly string[] {
  return isAllBusinessUnits(selected)
    ? BUSINESS_UNITS
    : BUSINESS_UNITS.filter((bu) => selected.includes(bu));
}

/** The target mode in effect: the marketer's choice, except that a narrowed BU forces By BU. */
export function effectiveTargetMode(chosen: TargetMode, selected: readonly string[]): TargetMode {
  return isAllBusinessUnits(selected) ? chosen : 'byBu';
}

/** The per-BU targets to send: only those whose BU still has a row. */
export function targetsForSave<T>(
  byBu: Readonly<Record<string, T>>,
  selected: readonly string[],
): [string, T][] {
  const rows = targetBusinessUnits(selected);
  return Object.entries(byBu).filter(([bu]) => rows.includes(bu));
}
